"use client";

import { Box, styled, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { InsuranceLeadModal } from "@/app/shared/insurance/InsuranceLeadModal";
import PetCoverReviews from "@/../public/img/petcover/reviews-white.png";
import PetCoverLogo from "@/../public/img/petcover/logo.png";
import PetCoverHeroMobile from "@/../public/img/petcover/hero-mobile.webp";
import Image from "next/image";
import { InsuranceLeadForm } from "@/app/shared/insurance/InsuranceLeadForm";
import {
  AD_DETAILS_BORDER_RADIUS,
  BOX_SHADOW_SIZE_LARGE,
} from "@/app/layout/LayoutConstants";
import { TOP_NAV_HEIGHT_DESKTOP_NUMBER } from "@/app/layout/GlobalNav";
import {
  AD_DETAILS_DESKTOP_SPACING,
  AD_DETAILS_MOBILE_SPACING,
} from "../for-sale/details/[slug]/[id]/components/AdDetailsPageLayout";
import Link from "next/link";
import PillButton from "@/app/shared/PillButton";
import LaunchIcon from "@mui/icons-material/Launch";

function InsuranceUSP({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "center",
      }}
    >
      <CheckIcon /> {children}
    </Typography>
  );
}

function InsuranceOptionCard({
  title,
  subtitle,
  list,
  caption,
}: {
  title: string;
  subtitle?: string;
  list: string[];
  caption?: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        backgroundColor: "white",
        padding: 2,
        borderRadius: 4,
        border: (theme) => `1px solid ${theme.palette.grey[300]}`,
      }}
    >
      <Typography
        sx={{
          fontWeight: "bold",
        }}
      >
        {title}
      </Typography>
      {subtitle && <Typography variant="subtitle1">{subtitle}</Typography>}
      <Box>
        {list.map((item, index) => (
          <InsuranceUSP key={index}>{item}</InsuranceUSP>
        ))}
      </Box>
      {caption && (
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {caption}
        </Typography>
      )}
    </Box>
  );
}

const Container = styled(Box)({
  maxWidth: "1300px",
  margin: "0 auto",
});

export function InsurancePage() {
  const CtaButtons = (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        alignSelf: "end",
      }}
    >
      <Link
        href="https://www.petcovergroup.com/au/"
        target="_blank"
        rel="noopener noreferrer"
      >
        <PillButton
          sx={{
            backgroundColor: "grey.200",
          }}
          endIcon={<LaunchIcon />}
        >
          Learn More
        </PillButton>
      </Link>
      <InsuranceLeadModal
        primary
        insurance={{
          adId: "",
          age: "",
          monthlyPremium: "",
        }}
        source="LANDING_PAGE"
      />
    </Box>
  );
  return (
    <Box
      sx={{
        backgroundColor: "grey.100",
        height: "100%",
      }}
    >
      <Box
        component={Image}
        src={PetCoverHeroMobile}
        alt="Horse Insurance"
        sx={{
          width: "100%",
          height: "auto",
          display: { xs: "block", md: "none" },
        }}
      />
      <Box
        sx={(theme) => ({
          background: `url('/img/petcover/hero.webp') no-repeat center top`,
          backgroundSize: "cover",
          [theme.breakpoints.down("md")]: {
            background: `none`,
          },
        })}
      >
        <Container
          sx={(theme) => ({
            padding: theme.spacing(10, 4),
            color: "white",
            [theme.breakpoints.down("md")]: {
              color: "black",
              padding: 2,
            },
          })}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={(theme) => ({
              [theme.breakpoints.down("md")]: {
                fontSize: "2rem",
              },
            })}
          >
            Horse & Pony Insurance Australia
          </Typography>
          <Typography variant="h6" sx={{ marginTop: 1 }}>
            Cover your horse or pony with a policy tailored to your needs,
          </Typography>
          <Typography variant="h6">starting from just $18 a month*</Typography>

          <Box
            sx={{
              display: "flex",
              gap: 4,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ marginTop: 2 }}>
              <InsuranceUSP>
                Claim up to 100% of eligible vet bills
              </InsuranceUSP>
              <InsuranceUSP>Personal Accident Cover</InsuranceUSP>
              <InsuranceUSP>Saddlery & tack</InsuranceUSP>
              <InsuranceUSP>
                Up to $10,000 annual cover for eligible vet bills
              </InsuranceUSP>
              <InsuranceUSP>Horse floats & horse-drawn vehicles</InsuranceUSP>
              <InsuranceUSP>
                Theft, straying & permanent loss of use
              </InsuranceUSP>
              <InsuranceUSP>Third party liability options</InsuranceUSP>
            </Box>
            <Box
              sx={{
                padding: 2,
                background: "white",
                borderRadius: 4,
              }}
            >
              <Link
                href="https://www.feefo.com/en-GB/reviews/petcovergroup"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src={PetCoverReviews}
                  alt="Petcover Reviews"
                  height={120}
                />
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
      <Container
        sx={(theme) => ({
          display: "grid",
          gridTemplateColumns: "1fr 400px",
          gap: 4,
          [theme.breakpoints.down("md")]: {
            gridTemplateColumns: "1fr",
          },
        })}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <Box
            sx={(theme) => ({
              display: "flex",
              flexDirection: "column",
              gap: 4,
              background: "white",
              padding: 4,
              borderRadius: theme.spacing(
                0,
                0,
                AD_DETAILS_BORDER_RADIUS,
                AD_DETAILS_BORDER_RADIUS,
              ),
              [theme.breakpoints.down("sm")]: {
                padding: 2,
              },
            })}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Link
                href="https://www.petcovergroup.com/au/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src={PetCoverLogo}
                  alt="Petcover Australia"
                  height={60}
                />
              </Link>
              <Typography sx={{ fontSize: "120%" }}>
                Horsezone and Petcover Australia have partnered to help more
                horse and pony owners across Australia protect the animals they
                love, with flexible cover options backed by specialist equine
                support. Petcover Australia is an award-winning insurer with a
                dedicated equine team and decades of experience insuring horses
                nationwide - from leisure riding to competition.
              </Typography>
              {CtaButtons}
            </Box>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                marginTop: 4,
              }}
            >
              What Your Policy Can Include
            </Typography>
            <Box
              sx={(theme) => ({
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 4,
                [theme.breakpoints.down("sm")]: {
                  gridTemplateColumns: "1fr",
                  gap: 2,
                },
              })}
            >
              <InsuranceOptionCard
                title="Vet & Health Cover (Horse & Pony)"
                list={[
                  "Up to $10,000 cover for eligible vet bills",
                  "Injury & illness treatment",
                  "Diagnostic tests & specialist referrals",
                ]}
              />
              <InsuranceOptionCard
                title="Life Stage & Loss Cover (Horse & Pony)"
                list={[
                  "Death from illness",
                  "Death from injury",
                  "Permanent loss of use",
                ]}
              />
              <InsuranceOptionCard
                title="Loss & Theft Cover"
                list={["Theft", "Straying"]}
              />

              <InsuranceOptionCard
                title="Gear & Transport Cover"
                list={[
                  "Saddlery & tack",
                  "Horse floats and transport vehicles",
                ]}
              />

              <InsuranceOptionCard
                title="Personal Accident Cover (Rider)"
                subtitle="Extra peace of mind while you ride"
                list={[
                  "Hospitalisation",
                  "Emergency dental",
                  "Temporary or permanent disability",
                ]}
                caption="and more following an accident while riding"
              />
            </Box>
            {CtaButtons}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                marginTop: 4,
              }}
            >
              <Box>
                <Typography variant="h4" component="h2">
                  Petcover Australia
                </Typography>
                <Typography variant="h6" component="h2">
                  Choose an Award-Winning Horse Insurance Service
                </Typography>
              </Box>
              <Typography>
                You’re in safe hands with Petcover’s team of equine specialists.
                They insure thousands of horses across Australia and understand
                first-hand what it means to own a horse and the health problems
                that can affect them.
              </Typography>
              <Typography>
                Their award-winning team is here to support you and your horse -
                whether that’s making a claim or helping you find the right
                cover.
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box>
          <Box
            sx={(theme) => ({
              position: "sticky",
              top:
                TOP_NAV_HEIGHT_DESKTOP_NUMBER +
                Number(theme.spacing(4).replace("px", "")),
              background: "white",
              borderRadius: AD_DETAILS_BORDER_RADIUS,
              margin: AD_DETAILS_DESKTOP_SPACING,
              marginLeft: 0,
              marginBottom: 0,
              padding: 3,
              boxShadow: BOX_SHADOW_SIZE_LARGE,

              [theme.breakpoints.down("md")]: {
                margin: AD_DETAILS_MOBILE_SPACING,
              },
            })}
          >
            <Typography
              variant="h4"
              component="h2"
              sx={{
                marginBottom: 4,
              }}
            >
              Get a Quote
            </Typography>
            <InsuranceLeadForm
              insurance={{
                adId: "",
                age: "",
                monthlyPremium: "",
              }}
              source="LANDING_PAGE"
            />
          </Box>
        </Box>
      </Container>
      <Container
        sx={{
          padding: 4,
        }}
      >
        <Typography variant="caption">
          *T&Cs apply. Prices may vary based on your horse or pony’s details,
          age, location and chosen cover level. Refer to the Product Disclosure
          Statement (PDS) for full terms, conditions, limits and exclusions.
        </Typography>
      </Container>
    </Box>
  );
}
